'use client';
import Button from '@/components/Button';
import Loader from '@/components/Loader';
import { NotifsContext } from '@/contexts';
import useBrand from '@/hooks/useBrand';
import useNavigate from '@/hooks/useNavigate';
import { useLazyGetEmployerQuery } from '@/services/API';
import { callback } from '@/services/AuthorizationService';
import { useAppDispatch } from '@/store/hooks';
import { logIn } from '@/store/slices/authSlice';
import { setSte } from '@/store/slices/steSlice';
import { AccountStatus } from '@/types';
import getEnv from '@/utils/env';
import { globalLogout } from '@/utils/LogoutUtils';
import { getURLParams } from '@/utils/router';
import { useContext, useEffect, useState } from 'react';
import styles from './page.module.scss';

export default function Page() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [getEmployer] = useLazyGetEmployerQuery();
  const searchParams = getURLParams();
  const brand = useBrand();
  const { addToast } = useContext(NotifsContext);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [isUnauthorized, setIsUnauthorized] = useState<boolean>(false);
  useEffect(() => {
    const env = getEnv();
    const error = searchParams.get('error');
    const errorDescription =
      searchParams.get('error_description') || 'Une erreur est survenue.';
    if (error) {
      let errorMessage = '';
      if (errorDescription === AccountStatus.DISABLED_PARTNER)
        errorMessage = 'Votre compte est désactivé.';
      else if (errorDescription === AccountStatus.DISABLED_BNPPF)
        errorMessage = 'Votre compte est bloqué.';
      else errorMessage = errorDescription;
      setIsLoading(false);
      addToast &&
        addToast({
          toastType: 'error',
          children: errorMessage,
        });
      return;
    }
    callback().then((userInfo) => {
      if (env.PROFIL == 'PF') {
        if (
          (env.env === 'PREPROD' || env.env === 'PROD') &&
          (!userInfo.habilitations ||
            userInfo.habilitations[0].ou !== env.ou ||
            userInfo.habilitations[0].roles[0].cn !== env.cn)
        ) {
          addToast &&
            addToast({
              toastType: 'error',
              children:
                "Vous n'avez pas le droit d'accéder à cette application",
            });
          navigate('logout');
        } else {
          dispatch(logIn());
          navigate('societe');
          window.history.replaceState(
            {},
            document.title,
            '/#/' + brand.get() + '/societe'
          );
        }
      } else {
        // get employer name
        getEmployer(userInfo.employer)
          .unwrap()
          .then((data) => {
            dispatch(logIn());
            const value = {
              label: data.name,
              value: data.name,
              ...data,
            };
            dispatch(setSte(value));
            navigate('menu');
            window.history.replaceState(
              {},
              document.title,
              '/#/' + brand.get() + '/menu'
            );
          })
          .catch((err) => {
            setIsLoading(false);
            setIsUnauthorized(true);
          });
      }
    });
  }, [, window.location]);

  const logout = () => {
    globalLogout({ brand: brand.get(), dispatch });
  };
  if (isLoading)
    return (
      <div className={styles['content']}>
        <p>redirection en cours...</p>
        <Loader />
      </div>
    );
  else
    return (
      <div className={styles['content']}>
        {isUnauthorized ? (
          <>
            <p>
              Vous n’êtes pas autorisé à accéder à cette application. Veuillez
              contacter votre administrateur.
            </p>
            <Button loading={false} onClick={logout}>
              Se déconnecter
            </Button>
          </>
        ) : (
          <>
            <p>Erreur de connexion</p>
            <Button loading={false} onClick={logout}>
              Se déconnecter
            </Button>
          </>
        )}
      </div>
    );
}
